import type { CSSProperties } from 'react';
import { isSpanStyleItem } from './span-style-item';
import type { SpanStyleItem } from './span-style-item';
import { resolveClassNames } from './resolve-class-names';
import { parseStyleDeclarations } from './parse-style-declarations';

/** The props a styled run's element receives, ready to spread. */
export type SpanStyleAttributes = { className?: string; style?: CSSProperties };

/**
 * React wants `fontWeight`, authors write `font-weight`. Custom properties keep
 * their name — React passes `--brand` through untouched — and a vendor prefix
 * loses its leading dash and gains a capital, except `-ms-`, which React spells
 * lowercase.
 */
const toReactProperty = (property: string): string => {
  if (property.startsWith('--')) {
    return property;
  }

  const isVendorPrefixed = property.startsWith('-');
  const camelCased = (isVendorPrefixed ? property.slice(1) : property).replace(
    /-([a-z])/g,
    (_, letter: string) => letter.toUpperCase()
  );

  if (!isVendorPrefixed || camelCased.startsWith('ms')) {
    return camelCased;
  }
  return camelCased.charAt(0).toUpperCase() + camelCased.slice(1);
};

/**
 * Turns a stored styling entry into element props, or `undefined` when the
 * entry cannot produce any — missing, cleared, or holding nothing that survived
 * the checks in `resolveClassNames` and `parseStyleDeclarations`. Callers render
 * the plain text in that case, exactly as they do for a link that resolves to
 * nothing.
 *
 * Knows nothing about text, markers, or any one component: this is the piece
 * every component rendering inline markup shares.
 */
export function resolveSpanStyleAttributes(
  item: SpanStyleItem | undefined
): SpanStyleAttributes | undefined {
  if (!isSpanStyleItem(item)) {
    return undefined;
  }

  const className = resolveClassNames(item.classNames);
  const declarations = parseStyleDeclarations(item.declarations);

  if (className === undefined && declarations.length === 0) {
    return undefined;
  }

  const style: Record<string, string> = {};
  declarations.forEach(({ property, value }) => {
    style[toReactProperty(property)] = value;
  });

  return {
    ...(className !== undefined ? { className } : {}),
    ...(declarations.length > 0 ? { style: style as CSSProperties } : {}),
  };
}
